import { createTheme, Loader, rem, virtualColor } from '@mantine/core';
import RingLoader from '@components/common/RingLoader';
/*
 *You can use colors generator to generate 10 shades of color based on a single value:
 *
 *colors: { 'pale-blue': generateColors('#375EAC'), },
 *That line goes into the createTheme colors below
 */

export const theme = createTheme({
  /** Controls focus ring styles. Supports the following options:
   *  - `auto` – focus ring is displayed only when the user navigates with keyboard (default value)
   *  - `always` – focus ring is displayed when the user navigates with keyboard and mouse
   *  - `never` – focus ring is always hidden (not recommended)
   */
  focusRing: 'auto',

  /** rem units scale, change if you customize font-size of `<html />` element
   *  default value is `1` (for `100%`/`16px` font-size on `<html />`)
   */
  scale: 1,

  /** Determines whether `font-smoothing` property should be set on the body, `true` by default */
  fontSmoothing: true,

  /** White color */
  white: '#ffffff',

  /** Black color */
  black: '#000000',

  /** Object of colors, key is color name, value is an array of at least 10 strings (colors) */
  colors: {
    customprimary: [
      '#e8fafc', '#dceef0', '#bed9dd', '#9bc3c9', '#7fb1b9',
      '#6ca6ae', '#60a0aa', '#4e8c95', '#407d86', '#2b6c76'
    ],
    customprimarydark: [
       '#dceef0','#bed9dd', '#9bc3c9', '#7fb1b9', '#6ca6ae', '#60a0aa',
      '#4e8c95', '#407d86', '#2b6c76', '#1a5d66'
    ],
    tesellylight: [
      '#f2f6f8', '#e7eaeb', '#c9d3d6', '#a7bcc2', '#8ca8b0',
      '#7a9ca6', '#6f96a2', '#5d828d', '#50747f', '#3e6570'
    ],
    tesellydark: [
      '#ebfcff', '#d7f7fc', '#aaeffb', '#7ce7fa', '#60e0f8',
      '#53dcf8', '#4bdaf9', '#3ec1de', '#2eacc6', '#0095ad'
    ],
    secondary: [
      '#ecf4ff', '#dde5f3', '#bac9e0', '#95aace', '#7591bf',
      '#6180b6', '#5678b2', '#46679d', '#3b5b8e', '#2d4f7f'
    ],
    success: [
      '#eafbf0', '#dcf1e4', '#bbdfc8', '#97ceab', '#78be92',
      '#64b581', '#59b179', '#489b66', '#3d8a5a', '#2d784b'
    ],
    warning: [
      '#fff5e3', '#fae9d2', '#f0d2a9', '#e6b97d', '#dea357',
      '#d9963e', '#d78f30', '#be7b22', '#aa6d1a', '#945d0e'
    ],
    danger: [
      '#ffebeb', '#fcd7d7', '#efadad', '#e38181', '#d95b5b',
      '#d44343', '#d23637', '#ba2829', '#a72123', '#93161c'
    ],
    info: [
      '#e5faff', '#d8eef4', '#b6dae4', '#90c4d3', '#71b2c5',
      '#5ca7bc', '#4fa2b9', '#3d8da3', '#2f7e93', '#136d82'
    ],
    dark: [
      '#C1C2C5', // 0 - light text
      '#A6A7AB', // 1 - secondary text
      '#909296', // 2 - dimmed text
      '#5C5F66', // 3 - subtle borders
      '#464a53', // 4 - borders
      '#363a43', // 5 - hover backgrounds
      '#2C2E33', // 6 - surface / card backgrounds
      '#25262B', // 7 - body background
      '#1A1B1E', // 8 - deep background
      '#141517' // 9 - deepest
    ],
    primary: virtualColor({
      name: 'primary',
      light: 'customprimary',
      dark: 'customprimarydark'
    })
  },

  primaryShade: { light: 6, dark: 7 },

  /** Key of `theme.colors`, hex/rgb/hsl values are not supported.
   *  Determines which color will be used in all components by default.
   *  Default value – `blue`.
   * */
  primaryColor: 'primary',

  /** Function to resolve colors based on variant.
   *  Can be used to deeply customize how colors are applied to `Button`, `ActionIcon`, `ThemeIcon`
   *  and other components that use colors from theme.
   * */
  // variantColorResolver: VariantColorsResolver;

  /** Determines whether text color must be changed based on the given `color` prop in filled variant
   *  For example, if you pass `color="blue.1"` to Button component, text color will be changed to `var(--mantine-color-black)`
   *  Default value – `false`
   * */
  // autoContrast: boolean;

  /** Determines which luminance value is used to determine if text color should be light or dark.
   *  Used only if `theme.autoContrast` is set to `true`.
   *  Default value is `0.3`
   * */
  // luminanceThreshold: number;

  /** font-family used in all components, system fonts by default */
  // fontFamily: string;
  fontFamily: 'Inter, sans-serif',

  /** Monospace font-family, used in code and other similar components, system fonts by default  */
  // fontFamilyMonospace: string;

  /** Controls various styles of h1-h6 elements, used in TypographyStylesProvider and Title components */
  // headings: {
  //   fontFamily: string;
  //   fontWeight: string;
  //   textWrap: 'wrap' | 'nowrap' | 'balance' | 'pretty' | 'stable';
  //   sizes: {
  //     h1: HeadingStyle;
  //     h2: HeadingStyle;
  //     h3: HeadingStyle;
  //     h4: HeadingStyle;
  //     h5: HeadingStyle;
  //     h6: HeadingStyle;
  //   };
  // };

  /** Object of values that are used to set `border-radius` in all components that support it */
  // radius: MantineRadiusValues;

  /** Key of `theme.radius` or any valid CSS value. Default `border-radius` used by most components */
  // defaultRadius: MantineRadius;

  /** Object of values that are used to set various CSS properties that control spacing between elements */
  // spacing: MantineSpacingValues;
  spacing: {
    none: rem( 0 ),
    xxs: rem( 6 ),
    xs: rem( 10 ),
    sm: rem( 12 ),
    md: rem( 16 ),
    lg: rem( 20 ),
    xl: rem( 32 )
  },
  /** Object of values that are used to control `font-size` property in all components */
  // fontSizes: MantineFontSizesValues;

  /** Object of values that are used to control `line-height` property in `Text` component */
  // lineHeights: MantineLineHeightValues;

  /** Object of values that are used to control breakpoints in all components,
   *  values are expected to be defined in em
   * */
  // breakpoints: MantineBreakpointsValues;
  other: {
    modalSizes: {
      intermediate: { width: '1050px', height: '85vh' },
      xxl: { width: '1200px', height: '85vh' },
      xxxl: { width: '1400px', height: '85vh' }
    },

    /**
     * Brand tokens that are NOT a shade of a palette.
     *
     * `headerDeep` is the solid fill behind a prominent, primary-coloured
     * header band — currently the dashboard's setup checklist. It is deliberately
     * DARKER than anything in `customprimary` (whose deepest shade is `#2b6c76`):
     * the band carries white text and needs the contrast, and picking
     * `primary-9` instead would tie the header to a palette step that exists to
     * serve buttons and borders, so any future palette tweak would silently
     * restyle it.
     *
     * It is very close to `customprimarydark[9]` (`#1a5d66`) — that is the
     * relationship, not a coincidence, and it is why the band reads as "the
     * brand, turned up" rather than as a foreign colour.
     *
     * ⚠️ Do NOT hardcode this hex in a CSS module. `cssVariablesResolver` in
     * `index.tsx` publishes it as `--teselly-header-deep`, derived from THIS
     * value, so there is exactly one place to change it.
     */
    brand: {
      headerDeep: '#1c5d65'
    }
  },

  /** Object of values that are used to add `box-shadow` styles to components that support `shadow` prop */
  // shadows: MantineShadowsValues;

  /** Determines whether user OS settings to reduce motion should be respected, `false` by default */
  // respectReducedMotion: boolean;

  /** Determines which cursor type will be used for interactive elements
   * - `default` – cursor that is used by native HTML elements, for example, `input[type="checkbox"]` has `cursor: default` styles
   * - `pointer` – sets `cursor: pointer` on interactive elements that do not have these styles by default
   */
  // cursorType: 'default' | 'pointer';

  /** Default gradient configuration for components that support `variant="gradient"` */
  // defaultGradient: MantineGradient;

  /** Class added to the elements that have active styles, for example, `Button` and `ActionIcon` */
  // activeClassName: string;

  /** Class added to the elements that have focus styles, for example, `Button` or `ActionIcon`.
   *  Overrides `theme.focusRing` property.
   */
  // focusClassName: string;

  /** Allows adding `classNames`, `styles` and `defaultProps` to any component */
  // components: MantineThemeComponents;
 components: {
    Loader: {
      defaultProps: {
        type: 'ring',
        loaders: {
          ...Loader.defaultLoaders,
          ring: RingLoader
        }
      }
    }
  }
});
